import { useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";

export interface FilterOption {
  value: string;
  label: string;
}

/** Rótulo padrão de um campo dentro do drawer de filtros. */
export function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
      {children}
    </div>
  );
}

interface SegmentedFilterProps {
  options: FilterOption[];
  value: string;
  onChange: (value: string) => void;
  allLabel?: string;
  className?: string;
}

/** Escolha única em linha; "Todos" (valor vazio) desliga o filtro. */
export function SegmentedFilter({
  options,
  value,
  onChange,
  allLabel = "Todos",
  className,
}: SegmentedFilterProps) {
  const items = [{ value: "", label: allLabel }, ...options];
  return (
    <div className={cn("flex flex-wrap gap-1", className)}>
      {items.map((option) => {
        const active = (value ?? "") === option.value;
        return (
          <button
            key={option.value || "__all"}
            type="button"
            onClick={() => onChange(option.value)}
            className={cn(
              "rounded-full border px-2.5 py-1 text-xs transition-colors",
              active
                ? "border-primary/40 bg-primary/10 font-medium text-primary"
                : "border-border bg-background text-muted-foreground hover:bg-accent",
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

interface FilterChipToggleProps {
  active: boolean;
  onClick: () => void;
  /** Estilo do estado ativo (cores de temperatura/eixo); default primary suave. */
  activeClassName?: string;
  title?: string;
  children: React.ReactNode;
}

/** Chip de múltipla escolha (um valor dentro de um filtro de array). */
export function FilterChipToggle({
  active,
  onClick,
  activeClassName,
  title,
  children,
}: FilterChipToggleProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-pressed={active}
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors",
        active
          ? cn(
              "font-medium",
              activeClassName ?? "border-primary/40 bg-primary/10 text-primary",
            )
          : "border-border bg-background text-muted-foreground hover:bg-accent",
      )}
    >
      {children}
    </button>
  );
}

interface FacetMultiSelectProps {
  placeholder: string;
  searchPlaceholder?: string;
  options: FilterOption[];
  selected: string[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
  /** true quando o caller renderiza a seleção do próprio jeito (ex.: linhas com slider). */
  hideSelected?: boolean;
}

/** Multi-select com busca (combobox); seleção vira chips removíveis abaixo. */
export function FacetMultiSelect({
  placeholder,
  searchPlaceholder,
  options,
  selected,
  onChange,
  disabled,
  hideSelected = false,
}: FacetMultiSelectProps) {
  const [open, setOpen] = useState(false);

  const toggleValue = (value: string) => {
    onChange(
      selected.includes(value)
        ? selected.filter((item) => item !== value)
        : [...selected, value],
    );
  };

  return (
    <div className="space-y-1.5">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="h-8 w-full justify-between px-2.5 text-xs font-normal text-muted-foreground"
          >
            {selected.length === 0
              ? placeholder
              : `${selected.length} ${selected.length === 1 ? "selecionado" : "selecionados"}`}
            <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-(--radix-popover-trigger-width) p-0"
          align="start"
        >
          <Command>
            <CommandInput
              placeholder={searchPlaceholder ?? "Buscar..."}
              className="h-8 text-xs"
            />
            <CommandList>
              <CommandEmpty>Nenhum resultado</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    onSelect={() => toggleValue(option.value)}
                    className="text-xs"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-3.5 w-3.5",
                        selected.includes(option.value)
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {!hideSelected && selected.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selected.map((value) => (
            <Badge
              key={value}
              variant="secondary"
              className="gap-1 px-2 py-0.5 text-[11px] font-normal"
            >
              {options.find((option) => option.value === value)?.label ?? value}
              <button
                type="button"
                onClick={() => toggleValue(value)}
                aria-label={`Remover ${value}`}
                className="hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
